import { SegmentedControl } from '@radix-ui/themes'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  BULK_EDIT_KEY,
  SplitData,
  clearBulkEdit,
  clearBulkSaveResults,
  getSplitsValidation,
  getTrackPublishingSplits,
  getTrackRecordingSplits,
  getTrackValidationErrors,
  hasSplitFieldValue,
  isBulkSaveLoading,
  isTrackDirty,
  isTrackLoading,
  markTrackSaved,
  setBulkSaveFailures,
  validatePublishingSplits,
  validateRecordingSplits,
  type BulkSaveTrackResult,
  type SplitsKey,
} from '@disco/state-management/reducers/rightsManagement'
import { useGetAllRightsHoldersQuery } from '@disco/state-management/api/rights-holders'
import {
  useBulkSaveRightsSplitsMutation,
  useReplaceTrackRightsSplitsMutation,
} from '@disco/state-management/api/rights-splits'
import { pushUINotification } from '@disco/utils/notifications'

import type {
  BulkRightsSplitsTrackEntry,
  BulkRightsSplitsTrackResult,
  ReplaceRightsSplitsRequest,
} from 'types/rights-data'
import type { EditableTrack, TrackId } from 'types/track'

import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import { Box, Button, Dialog, Flex, Text, Tooltip } from 'components/ds'
import DialogFooter from 'components/modals/dialog-footer'
import ButtonContainer from 'components/ui/button-container'
import FormNote from 'components/ui/form-note'
import InlineLink from 'components/ui/inline-link'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { getConstanceSettings } from '@disco/state-management/selectors/user'

import { ReviewTrackMetadataModal } from '../review-track-metadata-modal'
import styles from '../styles.module.css'
import { useLoadTrackRights } from './use-load-track-rights'
import { PublishingTab } from './publishing-tab'
import { RecordingTab } from './recording-tab'
import { humanizeBackendError, toChains } from './utils'
import { validateIswc } from 'features/rights-data/utils/identifier-validation'

type RightsTab = 'publishing' | 'recording'

type BulkSaveError = { body?: { results?: BulkRightsSplitsTrackResult[] } }

interface RightsManagementProps {
  editedTrack: EditableTrack & {
    trackIds?: TrackId[]
  }
  saving: boolean
  hasUnsavedChanges?: boolean
  updateEditedTrack: (track: any) => void
  saveTrackMetadata: (opts?: any) => void
  sendMixpanelEvent: (event: string) => void
  attach_writers_to_metadata: boolean
  attach_recording_rights_to_metadata: boolean
  /** Shows the table read-only (no editing, no save) for a read-only track */
  readOnly?: boolean
}

const RightsNote = ({
  category,
  isShared,
}: {
  category: 'Publishing' | 'Recording'
  isShared: boolean
}) =>
  isShared ? (
    <>
      {category} rights splits are currently shared with your tracks. Change
      this in <InlineLink to="/settings/business">business settings</InlineLink>
      .
    </>
  ) : (
    <>
      {category} rights splits are currently <u>not</u> shared with your tracks.
      Change this in{' '}
      <InlineLink to="/settings/business">business settings</InlineLink>.
    </>
  )

/**
 * RightsManagement - Container with Publishing/Recording segmented control
 * It provides a tabbed interface to manage publishing rights and recording rights
 */
export function RightsManagement({
  editedTrack,
  saving,
  hasUnsavedChanges,
  updateEditedTrack,
  saveTrackMetadata,
  sendMixpanelEvent,
  attach_writers_to_metadata,
  attach_recording_rights_to_metadata,
  readOnly = false,
}: RightsManagementProps) {
  const dispatch = useDispatch()

  const isBulkMode =
    editedTrack.id === null && (editedTrack.trackIds?.length ?? 0) > 0

  const isRecordingTabViewingAllowed =
    editedTrack.allow_viewing_recording_rights ?? true

  const isPublishingTabViewingAllowed =
    editedTrack.allow_viewing_publishing_rights ?? true

  const [activeTab, setActiveTab] = useState<RightsTab>(
    isPublishingTabViewingAllowed ? 'publishing' : 'recording',
  )

  const constanceSettings = useSelector(state => getConstanceSettings(state))
  const recordingTabEnabled =
    constanceSettings?.RIGHTS_DATA_SHOW_RECORDING_TAB || false

  const splitsKey: SplitsKey = isBulkMode ? BULK_EDIT_KEY : editedTrack.id!
  const hasIsrcValue = !!editedTrack.metadata?.ISRC?.value

  // RTK Query mutations for rights data API
  const [replaceRightsSplits, { isLoading: isReplacingLoading }] =
    useReplaceTrackRightsSplitsMutation()
  const [bulkSaveRightsSplits, { isLoading: isBulkSavingLoading }] =
    useBulkSaveRightsSplitsMutation()

  // Fetch all rights holders
  const { data: allRightsHolders = [], isLoading: isRightsHoldersLoading } =
    useGetAllRightsHoldersQuery()

  const publishingSplits = useSelector(state =>
    getTrackPublishingSplits(state, splitsKey),
  )
  const recordingSplits = useSelector(state =>
    getTrackRecordingSplits(state, splitsKey),
  )

  const splitsIsDirty = useSelector(state => isTrackDirty(state, splitsKey))
  const publishingIsSaving = useSelector(state =>
    isTrackLoading(state, splitsKey),
  )
  const splitsValidation = useSelector(state =>
    getSplitsValidation(state, splitsKey, recordingTabEnabled),
  )
  const validationErrors = useSelector(state =>
    getTrackValidationErrors(state, splitsKey),
  )
  const recordingValidation = useSelector(state =>
    validateRecordingSplits(state, splitsKey, recordingSplits),
  )
  const publishingValidation = useSelector(state =>
    validatePublishingSplits(state, splitsKey, publishingSplits),
  )

  const bulkSaveIsLoading = useSelector(isBulkSaveLoading)

  // Clear bulk edit state when component mounts in bulk mode
  useEffect(() => {
    if (isBulkMode) {
      dispatch(clearBulkEdit())
      dispatch(clearBulkSaveResults())
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    isInitialLoadComplete,
    isInitialLoading,
    isInitialLoadError,
    isRefetchError,
    refetchTrackRights,
  } = useLoadTrackRights(editedTrack.id ?? null, isBulkMode, splitsKey)

  // Memoize trackIds to prevent unnecessary re-renders in useCallback dependencies
  const trackIds = useMemo(
    () => editedTrack.trackIds ?? [],
    [editedTrack.trackIds],
  )

  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false)
  const [bulkAction, setBulkAction] = useState<'add' | 'replace' | null>(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [saveAttempted, setSaveAttempted] = useState(false)
  const [backendErrorMessage, setBackendErrorMessage] = useState('')
  const [backendErrorTab, setBackendErrorTab] = useState<
    'publishing' | 'recording' | null
  >(null)

  const iswcMeta = editedTrack.metadata?.ISWC
  const iswcValue = iswcMeta?.value ?? ''
  const iswcPreventsSave = !isBulkMode && !validateIswc(iswcValue).valid
  const hasIswcValue = !!iswcValue

  const hasRightsChanges = splitsIsDirty || hasUnsavedChanges
  const hasClientSideValidationErrors = validationErrors.length > 0

  // Check if there are valid (non-empty) splits to save
  const hasPublishingSplits = publishingSplits.some(split =>
    hasSplitFieldValue(split.writerId),
  )
  const hasRecordingSplits = recordingSplits.some(
    split =>
      (hasSplitFieldValue(split.rightsholderId) ||
        hasSplitFieldValue(split.labelId)) &&
      hasSplitFieldValue(split.split),
  )
  const hasValidSplits = hasPublishingSplits || hasRecordingSplits

  const noValidationErrors =
    !splitsValidation.errors.splitsTotalAbove100 &&
    !splitsValidation.errors.recSplitsTotalAbove100 &&
    !splitsValidation.errors.hasEmptyWriters &&
    !splitsValidation.errors.missingRightsholderAndLabel

  // Replace splits: always enabled in bulk mode (allow empty rows to clear all splits), but validates non-empty rows
  const canReplaceSplits = noValidationErrors

  const canSaveBulk = hasRightsChanges && noValidationErrors

  // Add splits: disabled when all rows are empty (nothing to add)
  const canAddSplits = hasValidSplits && noValidationErrors

  // Save metadata only: enabled when there are metadata changes e.g PRO Number but no valid splits
  // This allows saving metadata fields without adding splits
  const canSaveMetadataOnly =
    hasUnsavedChanges &&
    !iswcPreventsSave &&
    !hasValidSplits &&
    !splitsValidation.errors.hasEmptyWriters &&
    !splitsValidation.errors.missingRightsholderAndLabel

  const canSave = isBulkMode
    ? canSaveBulk
    : hasRightsChanges &&
      splitsValidation.isValid &&
      !iswcPreventsSave &&
      !hasClientSideValidationErrors

  const isSaveButtonDisabled = isBulkMode
    ? !canSaveBulk
    : !hasRightsChanges || !splitsValidation.isValid || iswcPreventsSave

  const isLoading =
    saving ||
    publishingIsSaving ||
    bulkSaveIsLoading ||
    isReplacingLoading ||
    isBulkSavingLoading

  const rightsEditingDisabled = readOnly || isLoading || !isInitialLoadComplete

  const handleSaveInternal = useCallback(async () => {
    if (!editedTrack.id) {
      return
    }

    setBackendErrorMessage('')
    setBackendErrorTab(null)

    try {
      // Filter out empty placeholder rows from publishing splits

      const publishingChains = toChains(
        publishingSplits,
        split => typeof split.writerId === 'number' && split.writerId > 0,
      )

      const recordingChains = toChains(
        recordingSplits,
        split =>
          hasSplitFieldValue(split.rightsholderId) ||
          hasSplitFieldValue(split.labelId),
      )

      const data: ReplaceRightsSplitsRequest = {
        rights_splits: [],
      }

      if (publishingChains.length > 0 || splitsIsDirty) {
        data.rights_splits.push({
          rights_type: 'publishing',
          chains: publishingChains,
        })
      }

      if (
        recordingTabEnabled &&
        (recordingChains.length > 0 || splitsIsDirty)
      ) {
        data.rights_splits.push({
          rights_type: 'recording',
          chains: recordingChains,
        })
      }

      await replaceRightsSplits({
        trackId: editedTrack.id,
        data,
      }).unwrap()

      dispatch(markTrackSaved(editedTrack.id))
      setSaveAttempted(false)
      saveTrackMetadata()
      sendMixpanelEvent('Metadata edit')
    } catch (error) {
      try {
        setBackendErrorTab(error.body.rights_splits['0'].rights_type)
        setBackendErrorMessage(error.body.rights_splits['0'].errors)
      } catch (_) {
        setBackendErrorMessage('Failed to save rights.')
        setBackendErrorTab(null)
      }
    }
  }, [
    dispatch,
    editedTrack.id,
    publishingSplits,
    recordingSplits,
    recordingTabEnabled,
    splitsIsDirty,
    replaceRightsSplits,
    saveTrackMetadata,
    sendMixpanelEvent,
  ])

  const handleSave = useCallback(() => {
    setSaveAttempted(true)
    if (canSave) {
      handleSaveInternal()
    }
  }, [canSave, handleSaveInternal])

  const handleBulkSave = useCallback(
    async (overwrite: boolean) => {
      if (trackIds.length === 0) {
        pushUINotification({
          message: 'No tracks selected for bulk editing.',
          level: 'error',
        })
        return
      }

      dispatch(clearBulkSaveResults())

      const publishingChains = toChains(publishingSplits, split =>
        hasSplitFieldValue(split.writerId),
      )

      const recordingChains = toChains(
        recordingSplits,
        split =>
          hasSplitFieldValue(split.rightsholderId) ||
          hasSplitFieldValue(split.labelId),
      )

      // For overwrite (replace), include a tab even with empty chains so the
      // server clears existing splits for that rights_type. For append, skip
      // empty tabs — nothing to add.
      const tracks: BulkRightsSplitsTrackEntry[] = []
      trackIds.forEach(track_id => {
        if (overwrite || publishingChains.length > 0) {
          tracks.push({
            track_id,
            rights_type: 'publishing',
            overwrite,
            chains: publishingChains,
          })
        }
        if (recordingTabEnabled && (overwrite || recordingChains.length > 0)) {
          tracks.push({
            track_id,
            rights_type: 'recording',
            overwrite,
            chains: recordingChains,
          })
        }
      })

      if (tracks.length === 0) {
        return
      }

      const action = overwrite ? 'replaced on' : 'added to'

      try {
        const result = await bulkSaveRightsSplits({
          data: { tracks },
          atomic: true,
        }).unwrap()

        // Each (track_id, rights_type) is its own row, so a track can appear
        // twice. Dedupe by track_id, and filter to actual successes — explicit
        // in case the API ever returns 200 with mixed statuses (non-atomic).
        const uniqueTrackIds = new Set(
          result.results
            .filter(r => r.status === 'success')
            .map(r => r.track_id),
        )
        const successCount = uniqueTrackIds.size

        pushUINotification({
          message: `Rights splits ${action} ${successCount} track${
            successCount !== 1 ? 's' : ''
          } successfully.`,
          level: 'success',
        })

        dispatch(clearBulkEdit())
        saveTrackMetadata()
        sendMixpanelEvent('Bulk metadata edit')
      } catch (err) {
        const error = err as BulkSaveError
        const resultRows = error?.body?.results ?? []

        // Each track may appear twice (publishing + recording rows) — collect all
        // error messages per track and dedupe by track_id.
        const errorsByTrack = new Map<number, string[]>()
        resultRows.forEach(r => {
          if (r.status === 'success') return
          const messages = errorsByTrack.get(r.track_id) ?? []
          if (r.errors) {
            messages.push(humanizeBackendError(r.errors))
          }
          errorsByTrack.set(r.track_id, messages)
        })

        const failures: BulkSaveTrackResult[] = []
        errorsByTrack.forEach((messages, trackId) => {
          failures.push({
            trackId,
            success: false,
            errors:
              messages.length > 0
                ? messages
                : ['Rolled back due to another track’s failure'],
          })
        })

        if (failures.length > 0) {
          dispatch(setBulkSaveFailures(failures))
          setShowReviewModal(true)
          pushUINotification({
            message: `${failures.length} track${
              failures.length !== 1 ? 's' : ''
            } failed — review details.`,
            level: 'error',
          })
          return
        }

        // No structured per-track results (e.g. network error) — generic toast.
        pushUINotification({
          message: 'Failed to save rights',
          level: 'error',
        })
      }
    },
    [
      dispatch,
      trackIds,
      publishingSplits,
      recordingSplits,
      recordingTabEnabled,
      bulkSaveRightsSplits,
      saveTrackMetadata,
      sendMixpanelEvent,
    ],
  )

  const handleAddSplits = async () => {
    setBulkAction('add')
    await handleBulkSave(false)
    setBulkAction(null)
  }

  const handleReplaceSplits = async () => {
    setBulkAction('replace')
    await handleBulkSave(true)
    setBulkAction(null)
  }

  // Save metadata only (PRO number, etc.) without modifying splits
  const handleSaveMetadataOnly = () => {
    saveTrackMetadata()
    sendMixpanelEvent('Bulk metadata edit')
  }

  if (readOnly && isInitialLoading) {
    return (
      <Flex align="center" justify="center" py="6">
        <Text size="2" color="gray">
          Loading rights…
        </Text>
      </Flex>
    )
  }

  if (readOnly && isInitialLoadError) {
    return (
      <Flex align="center" justify="center" gap="2" py="6">
        <FormNote displayIcon>
          We could not load the rights for this track.
        </FormNote>
        <Button
          variant="soft"
          color="gray"
          size="1"
          onClick={() => refetchTrackRights()}
        >
          Retry
        </Button>
      </Flex>
    )
  }

  // Shows an empty state once it's clear there's no actual data (the tab
  // itself is hidden by the parent once onDataLoaded above reports false —
  // this is the brief window before that happens, or a fallback if the
  // parent doesn't wire up onDataLoaded).
  // ISRC/ISWC count as rights data even with no splits recorded.
  if (readOnly && !hasValidSplits && !hasIsrcValue && !hasIswcValue) {
    return (
      <Flex align="center" justify="center" py="6">
        <Text size="2" color="gray">
          No rights data available
        </Text>
      </Flex>
    )
  }

  return (
    <Box>
      {recordingTabEnabled &&
        isPublishingTabViewingAllowed &&
        isRecordingTabViewingAllowed && (
          <Box mb="4">
            <SegmentedControl.Root
              // SLAM-1943: Set these explicitly. They previously came from a global defaultProps mutation that React 19 ignores on function components.
              size="2"
              variant="classic"
              radius="large"
              value={activeTab}
              onValueChange={value => setActiveTab(value as RightsTab)}
              className={styles.tabs}
            >
              <SegmentedControl.Item value="publishing">
                Publishing
                {(!publishingValidation.isValid ||
                  backendErrorTab === 'publishing') && (
                  <span className={styles.errorDot}>•</span>
                )}
              </SegmentedControl.Item>
              <SegmentedControl.Item value="recording">
                Recording
                {(!recordingValidation.isValid ||
                  backendErrorTab === 'recording') && (
                  <span className={styles.errorDot}>•</span>
                )}
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Box>
        )}

      {isInitialLoadError && !readOnly && (
        <Box mb="4">
          <Flex align="center" gap="2">
            <FormNote displayIcon>
              We could not load the existing rights for this track. Editing is
              disabled to avoid overwriting them.
            </FormNote>
            <Button
              variant="soft"
              color="gray"
              size="1"
              onClick={() => refetchTrackRights()}
            >
              Retry
            </Button>
          </Flex>
        </Box>
      )}
      {isRefetchError && !readOnly && (
        <Box mb="4">
          <Flex align="center" gap="2">
            <FormNote displayIcon>
              We could not refresh the rights for this track. Your edits are
              based on the last loaded data.
            </FormNote>
            <Button
              variant="soft"
              color="gray"
              size="1"
              onClick={() => refetchTrackRights()}
            >
              Retry
            </Button>
          </Flex>
        </Box>
      )}
      {isInitialLoading && (
        <Box mb="4">
          <FormNote displayIcon={false}>Loading rights…</FormNote>
        </Box>
      )}

      {activeTab === 'publishing' ? (
        readOnly && !hasPublishingSplits && !hasIswcValue ? (
          <Flex align="center" justify="center" py="6">
            <Text size="2" color="gray">
              No publishing rights available
            </Text>
          </Flex>
        ) : (
          <PublishingTab
            editedTrack={editedTrack}
            updateEditedTrack={updateEditedTrack}
            isBulkMode={isBulkMode}
            splitsKey={splitsKey}
            saveAttempted={saveAttempted}
            allRightsHolders={allRightsHolders}
            isRightsHoldersLoading={isRightsHoldersLoading}
            backendErrorMessage={backendErrorMessage}
            backendErrorTab={backendErrorTab}
            recordingTabEnabled={recordingTabEnabled}
            disabled={isLoading || readOnly}
            splitsDisabled={rightsEditingDisabled}
            readOnly={readOnly}
            showSplits={
              !readOnly || (attach_writers_to_metadata && hasPublishingSplits)
            }
          />
        )
      ) : readOnly && !hasRecordingSplits && !hasIsrcValue ? (
        <Flex align="center" justify="center" py="6">
          <Text size="2" color="gray">
            No recording rights available
          </Text>
        </Flex>
      ) : (
        recordingTabEnabled && (
          <RecordingTab
            editedTrack={editedTrack}
            updateEditedTrack={updateEditedTrack}
            splitsKey={splitsKey}
            isBulkMode={isBulkMode}
            saveAttempted={saveAttempted}
            allRightsHolders={allRightsHolders}
            isRightsHoldersLoading={isRightsHoldersLoading}
            backendErrorMessage={backendErrorMessage}
            backendErrorTab={backendErrorTab}
            disabled={isLoading || readOnly}
            splitsDisabled={rightsEditingDisabled}
            readOnly={readOnly}
            showSplits={!readOnly || hasRecordingSplits}
          />
        )
      )}

      {activeTab === 'publishing' && !readOnly && (
        <Box mb="4">
          <FormNote displayIcon={false}>
            <RightsNote
              category="Publishing"
              isShared={attach_writers_to_metadata}
            />
          </FormNote>
        </Box>
      )}

      {activeTab === 'recording' && !readOnly && (
        <Box mb="4">
          <FormNote displayIcon={false}>
            <RightsNote
              category="Recording"
              isShared={attach_recording_rights_to_metadata}
            />
          </FormNote>
        </Box>
      )}

      {!readOnly &&
        (isBulkMode ? (
          <DialogFooter
            primary={
              <ButtonContainer>
                <CopyMetadataButton
                  trackIds={editedTrack.trackIds ?? []}
                  variant="inline"
                />
                <Tooltip content="Replace splits clears all existing splits and replaces them with those above">
                  <Button
                    variant="soft"
                    color="gray"
                    onClick={() => setShowReplaceConfirm(true)}
                    disabled={
                      !canReplaceSplits ||
                      (isLoading && bulkAction !== 'replace')
                    }
                    loading={isLoading && bulkAction === 'replace'}
                  >
                    Replace splits
                  </Button>
                </Tooltip>
                {hasValidSplits ? (
                  <Tooltip content="Add splits keeps existing splits and adds those above to each track">
                    <Button
                      onClick={handleAddSplits}
                      disabled={
                        readOnly ||
                        !canAddSplits ||
                        (isLoading && bulkAction !== 'add')
                      }
                      loading={isLoading && bulkAction === 'add'}
                    >
                      Add splits
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={handleSaveMetadataOnly}
                    disabled={readOnly || !canSaveMetadataOnly || isLoading}
                    loading={isLoading && bulkAction === null}
                  >
                    Save
                  </Button>
                )}
              </ButtonContainer>
            }
          />
        ) : (
          <DialogFooter
            primary={
              <ButtonContainer>
                {editedTrack.id && (
                  <CopyMetadataButton trackIds={[editedTrack.id]} />
                )}
                {hasValidSplits || splitsIsDirty ? (
                  <Button
                    onClick={handleSave}
                    disabled={isSaveButtonDisabled || rightsEditingDisabled}
                    loading={isLoading}
                  >
                    Save
                  </Button>
                ) : (
                  <Button
                    onClick={handleSaveMetadataOnly}
                    disabled={!canSaveMetadataOnly}
                  >
                    Save
                  </Button>
                )}
              </ButtonContainer>
            }
          />
        ))}

      <ReviewTrackMetadataModal
        open={showReviewModal}
        onClose={() => {
          setShowReviewModal(false)
          dispatch(clearBulkSaveResults())
        }}
      />

      <Dialog.Root
        open={showReplaceConfirm}
        onOpenChange={setShowReplaceConfirm}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Replace all splits?</Dialog.Title>
          <Dialog.Description size="2">
            {`This will clear all existing splits on ${
              editedTrack.trackIds?.length ?? 0
            } selected track${
              (editedTrack.trackIds?.length ?? 0) !== 1 ? 's' : ''
            } and replace them with the splits defined above. This cannot be undone.`}
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" size="2">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="red"
              size="2"
              disabled={isLoading}
              loading={isLoading}
              onClick={() => {
                setShowReplaceConfirm(false)
                handleReplaceSplits().catch(() => {})
              }}
            >
              Replace splits
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  )
}

export default RightsManagement
